import {
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useState } from 'react'
import { Dialog } from '@/components/AppDialog'
import {
  useOutlets,
  useRemoveScheduledReport,
  useSaveScheduledReport,
  useScheduledReports,
} from '@/api/hooks'
import type { ScheduledReportView } from '@/api/types'
import { REPORTS } from '@/pages/reports/catalogue'
import { useAuthStore } from '@/auth/authStore'
import { EmptyState, ErrorBanner, formatDateTime, humanise, PageHeader } from '@/components/common'

/**
 * Reports built for one customer or one kitchen at a time have nobody for a
 * clock to name, so they are left off what can be scheduled.
 */
const NOT_SCHEDULABLE = new Set(['CUSTOMER_HISTORY', 'KITCHEN_PRODUCTION_SHEET'])
const SCHEDULABLE_REPORTS = REPORTS.filter((report) => !NOT_SCHEDULABLE.has(report.type))

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' },
]

const reportLabel = (type: string) => SCHEDULABLE_REPORTS.find((r) => r.type === type)?.name ?? humanise(type)

/** When, in the words an office would say it. */
function schedule(report: ScheduledReportView) {
  const time = report.timeOfDay.slice(0, 5)
  if (report.frequency === 'WEEKLY') {
    const day = DAYS_OF_WEEK.find((d) => d.value === report.dayOfWeek)?.label ?? ''
    return `Every ${day} at ${time}`
  }
  if (report.frequency === 'MONTHLY') {
    return `Day ${report.dayOfMonth} of the month at ${time}`
  }
  return `Every day at ${time}`
}

/**
 * Reports that send themselves, rather than being asked for on the reports
 * screen each time. A daily report covers the day just finished; weekly, the
 * last seven days; monthly, last calendar month - never the one still
 * running.
 */
export function ScheduledReportsPage() {
  const can = useAuthStore((state) => state.can)
  // Reaching this screen at all already means report.schedule is held -
  // there is no separate read/write split for it, unlike most modules.
  const writable = can('report.schedule')
  const { data: reports, error } = useScheduledReports()
  const { data: outlets } = useOutlets()
  const save = useSaveScheduledReport()
  const remove = useRemoveScheduledReport()
  const [editing, setEditing] = useState<ScheduledReportView | null>(null)
  const [adding, setAdding] = useState(false)

  const outletName = (id?: string) => (outlets ?? []).find((o) => o.id === id)?.name

  return (
    <>
      <PageHeader
        title="Scheduled reports"
        subtitle="Reports sent by email or WhatsApp on their own, at the time named — nobody has to come and ask for them."
        actions={
          writable ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
              New schedule
            </Button>
          ) : null
        }
      />

      <ErrorBanner error={error ?? save.error ?? remove.error} />

      {(reports ?? []).length === 0 ? (
        <EmptyState
          message="No report is scheduled yet."
          hint="Add one to have it emailed or sent on WhatsApp on its own, at the time you name."
        />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Report</TableCell>
              <TableCell>When</TableCell>
              <TableCell>Branch</TableCell>
              <TableCell>Sent to</TableCell>
              <TableCell>Last sent</TableCell>
              <TableCell align="center">On</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {(reports ?? []).map((report) => (
              <TableRow key={report.id} hover>
                <TableCell>{reportLabel(report.reportType)}</TableCell>
                <TableCell>{schedule(report)}</TableCell>
                <TableCell>{outletName(report.outletId) ?? 'Every branch'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {report.recipientEmails ? <Chip size="small" label={`Email ×${report.recipientEmails.split(',').length}`} /> : null}
                    {report.recipientWhatsappNumbers ? (
                      <Chip size="small" label={`WhatsApp ×${report.recipientWhatsappNumbers.split(',').length}`} />
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {report.lastSentAt ? formatDateTime(report.lastSentAt) : 'Not yet'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={report.active}
                    disabled={!writable || save.isPending}
                    onChange={(event) =>
                      save.mutate({
                        id: report.id,
                        body: { ...toRequest(report), active: event.target.checked },
                      })
                    }
                  />
                </TableCell>
                <TableCell align="right">
                  {writable ? (
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Correct this schedule">
                        <IconButton size="small" onClick={() => setEditing(report)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Stop this report sending itself">
                        <IconButton size="small" onClick={() => remove.mutate(report.id)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ScheduleDialog
        open={adding || Boolean(editing)}
        report={editing}
        onClose={() => {
          setAdding(false)
          setEditing(null)
        }}
      />
    </>
  )
}

/** What the server reads back from a row, shaped as the save request it already is. */
function toRequest(report: ScheduledReportView) {
  return {
    reportType: report.reportType,
    outletId: report.outletId,
    frequency: report.frequency,
    dayOfWeek: report.dayOfWeek,
    dayOfMonth: report.dayOfMonth,
    timeOfDay: report.timeOfDay,
    recipientEmails: report.recipientEmails,
    recipientWhatsappNumbers: report.recipientWhatsappNumbers,
  }
}

function ScheduleDialog({
  open,
  report,
  onClose,
}: {
  open: boolean
  report: ScheduledReportView | null
  onClose: () => void
}) {
  const save = useSaveScheduledReport()
  const { data: outlets } = useOutlets()
  const editing = Boolean(report)

  const [reportType, setReportType] = useState('')
  const [outletId, setOutletId] = useState('')
  const [frequency, setFrequency] = useState('DAILY')
  const [dayOfWeek, setDayOfWeek] = useState(1)
  const [dayOfMonth, setDayOfMonth] = useState(1)
  const [timeOfDay, setTimeOfDay] = useState('09:00')
  const [emails, setEmails] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [active, setActive] = useState(true)
  const [loaded, setLoaded] = useState<string | null>(null)

  const key = report?.id ?? (open ? 'new' : null)
  if (key && loaded !== key) {
    setLoaded(key)
    setReportType(report?.reportType ?? '')
    setOutletId(report?.outletId ?? '')
    setFrequency(report?.frequency ?? 'DAILY')
    setDayOfWeek(report?.dayOfWeek ?? 1)
    setDayOfMonth(report?.dayOfMonth ?? 1)
    setTimeOfDay(report?.timeOfDay.slice(0, 5) ?? '09:00')
    setEmails(report?.recipientEmails ?? '')
    setWhatsapp(report?.recipientWhatsappNumbers ?? '')
    setActive(report?.active ?? true)
  }

  const valid =
    Boolean(reportType)
    && (emails.trim() !== '' || whatsapp.trim() !== '')
    && (frequency !== 'WEEKLY' || Boolean(dayOfWeek))
    && (frequency !== 'MONTHLY' || Boolean(dayOfMonth))

  const submit = () => {
    save.mutate(
      {
        id: report?.id,
        body: {
          reportType,
          outletId: outletId || undefined,
          frequency,
          dayOfWeek: frequency === 'WEEKLY' ? dayOfWeek : undefined,
          dayOfMonth: frequency === 'MONTHLY' ? dayOfMonth : undefined,
          timeOfDay: `${timeOfDay}:00`,
          recipientEmails: emails.trim() || undefined,
          recipientWhatsappNumbers: whatsapp.trim() || undefined,
          active,
        },
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? 'Correct this schedule' : 'Schedule a report'}</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={save.error} />
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="Report"
            value={reportType}
            onChange={(event) => setReportType(event.target.value)}
            required
          >
            {SCHEDULABLE_REPORTS.map((r) => (
              <MenuItem key={r.type} value={r.type}>
                {r.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Branch"
            value={outletId}
            onChange={(event) => setOutletId(event.target.value)}
            helperText="Left blank, the report covers every branch"
          >
            <MenuItem value="">Every branch</MenuItem>
            {(outlets ?? []).map((outlet) => (
              <MenuItem key={outlet.id} value={outlet.id}>
                {outlet.name}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Frequency"
              value={frequency}
              onChange={(event) => setFrequency(event.target.value)}
              sx={{ flex: 1 }}
            >
              <MenuItem value="DAILY">Daily — the day just finished</MenuItem>
              <MenuItem value="WEEKLY">Weekly — the last 7 days</MenuItem>
              <MenuItem value="MONTHLY">Monthly — last calendar month</MenuItem>
            </TextField>
            {frequency === 'WEEKLY' ? (
              <TextField
                select
                label="Day"
                value={dayOfWeek}
                onChange={(event) => setDayOfWeek(Number(event.target.value))}
                sx={{ width: 160 }}
              >
                {DAYS_OF_WEEK.map((d) => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            {frequency === 'MONTHLY' ? (
              <TextField
                type="number"
                label="Day of month"
                value={dayOfMonth}
                onChange={(event) => setDayOfMonth(Number(event.target.value))}
                inputProps={{ min: 1, max: 28 }}
                sx={{ width: 160 }}
              />
            ) : null}
            <TextField
              type="time"
              label="Time"
              value={timeOfDay}
              onChange={(event) => setTimeOfDay(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />
          </Stack>
          <TextField
            label="Email addresses"
            value={emails}
            onChange={(event) => setEmails(event.target.value)}
            placeholder="owner@example.com, accounts@example.com"
            helperText="Separated by commas"
            multiline
            minRows={2}
          />
          <TextField
            label="WhatsApp numbers"
            value={whatsapp}
            onChange={(event) => setWhatsapp(event.target.value)}
            placeholder="98765 43210, 91234 56789"
            helperText="Separated by commas"
          />
          <FormControlLabel
            control={<Switch checked={active} onChange={(event) => setActive(event.target.checked)} />}
            label={active ? 'Enabled — sends on its own' : 'Disabled — kept, but never sends'}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!valid || save.isPending} onClick={submit}>
          {editing ? 'Save' : 'Schedule it'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
